import { mainUrl } from "../../utils/variant";
import React from 'react';
import DashboardPreview from '../DashboardPreview';
import { useVariantCopy } from './useVariantCopy';

// Variant landing-page hero (startviralgpt.com). Clone of components/HomeHeader.js.
// Every CTA scrolls to the pricing cards rendered below (#pricing); only the plan
// buttons inside variant/Pricing.js hop to the main site to start checkout.
const Header = () => {

	const L = useVariantCopy();
	const [currentIcon, setCurrentIcon] = React.useState(0);
	const [showVideo, setShowVideo] = React.useState(false);
	const icons = [
		{ icon: "fa-instagram", color: "text-instagram" },
		{ icon: "fa-tiktok", color: "text-tiktok" },
		{ icon: "fa-youtube", color: "text-danger" },
		{ icon: "fa-twitter-x", color: "text-white bg-dark rounded" }
	];

	React.useEffect(() => {
		const timer = setInterval(() => {
			setCurrentIcon((prev) => (prev + 1) % icons.length);
		}, 3000);

		return () => clearInterval(timer);
	}, []);

	// Hide fixed navbar + sticky CTA while the how-it-works video is open
	// (Bootstrap fixed-top is z-index 1030, above a normal modal).
	React.useEffect(() => {
		document.body.classList.toggle('video-modal-open', showVideo);
		return () => document.body.classList.remove('video-modal-open');
	}, [showVideo]);

	return (
		<>
		<header className="stripe-section--gradient pt-6 md:pt-12 pb-8 md:pb-10">
			<div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Hero Section */}
				<div className="text-center max-w-3xl mx-auto">
					{/* Headline — solid brand navy, sized to stay above the fold on mobile */}
					<h1 className="font-extrabold tracking-tight text-[2.15rem] leading-[1.15] sm:text-5xl md:text-6xl stripe-text-dark">
						{L.header.heroHeadline}
					</h1>

					<p className="mt-3 md:mt-4 text-base md:text-xl stripe-text-body max-w-2xl mx-auto leading-relaxed px-2">
						{L.header.heroSub}
					</p>

					{/* The only CTA. Goes straight to the main site's pricing page,
					    which now carries both the plan picker and the free follower
					    boost — so sending people to /visualizer for the boost first
					    would be a detour through a page whose content moved. */}
					<div className="mt-5 md:mt-7 px-4 sm:px-0">
						<a
							href={mainUrl("/pricing")}
							className="group inline-flex items-center justify-center gap-2 w-full sm:w-80 px-6 md:px-8 py-4 text-lg font-bold text-white rounded-xl transition-all duration-200 hover:scale-[1.02] no-underline"
							style={{
								background: 'linear-gradient(135deg, #635bff 0%, #7c3aed 100%)',
								boxShadow: '0 6px 18px rgba(99, 91, 255, 0.35)',
							}}
						>
							{L.header.heroCta}
							<i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1"></i>
						</a>

					</div>

					<div
						className="mt-4 mx-auto inline-flex flex-col sm:flex-row items-center justify-center gap-y-1 sm:gap-x-3 text-[0.76rem] md:text-sm stripe-text-muted"
						style={{ background: 'rgba(255,255,255,0.82)', border: '1px solid #e3e8ee', borderRadius: 999, padding: '7px 13px' }}
					>
						<span className="inline-flex items-center gap-1 whitespace-nowrap">
							<i className="fa-solid fa-star text-yellow-500 text-[0.7rem]"></i>
							<strong className="stripe-text-dark">{L.header.proofRating}</strong>
							<span>({L.header.proofReviews})</span>
						</span>
						<span className="hidden sm:inline text-gray-300">•</span>
						<span className="whitespace-nowrap"><strong className="stripe-text-dark">{L.header.proofFollowers}</strong></span>
					</div>

				</div>

				{/* Trust band — verified figures from our own delivery data */}
				<div className="mt-10 md:mt-14 max-w-4xl mx-auto text-center px-2">
					<h2 className="stripe-heading stripe-heading--sm md:stripe-heading--md">{L.header.trustHeading}</h2>
					<p className="mt-2 stripe-body stripe-body--sm stripe-text-muted max-w-2xl mx-auto">{L.header.trustSub}</p>

					<div className="mt-6 flex flex-wrap items-center justify-center gap-3 md:gap-4 max-w-4xl mx-auto">
						{[
							{ src: '/images/b.webp', alt: 'BuzzFeed' },
							{ src: '/images/f.webp', alt: 'FabFitFun' },
							{ src: '/images/l.webp', alt: "L'Oréal" },
							{ src: '/images/p.webp', alt: 'Proactiv' },
							{ src: '/images/u.webp', alt: 'Uber' },
						].map((logo) => (
							<div
								key={logo.alt}
								className="rounded-2xl px-4 py-4 md:px-5 md:py-5 flex items-center justify-center"
								style={{
									background: '#fff',
									border: '1px solid #e3e8ee',
									boxShadow: '0 2px 4px rgba(50,50,93,0.06)',
									width: 'clamp(7.5rem, 28vw, 9.5rem)',
									height: '4.5rem',
								}}
							>
								<img
									src={logo.src}
									alt={logo.alt}
									className="max-h-8 md:max-h-9 w-auto max-w-full object-contain"
									loading="lazy"
								/>
							</div>
						))}
					</div>

					{/* Video sits between the trust band and the sections below it */}
					<div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
						<button
							onClick={() => setShowVideo(true)}
							className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 md:px-8 py-3.5 md:py-4 text-base md:text-lg font-semibold rounded-xl transition-all duration-200 hover:scale-105 cursor-pointer"
							style={{ color: '#425466', background: '#fff', border: '1px solid #e3e8ee', boxShadow: '0 2px 4px rgba(50,50,93,0.08)' }}
						>
							<i className="fa-solid fa-play text-purple-500 text-sm"></i>
							{L.header.watchHowItWorks}
						</button>
					</div>

					{/* Featured In — press logos */}
					<div className="mt-10 md:mt-12">
						<p
							className="text-center mb-4 md:mb-5"
							style={{
								fontSize: '0.7rem',
								fontWeight: 600,
								letterSpacing: '0.12em',
								textTransform: 'uppercase',
								color: '#94a3b8',
							}}
						>
							{L.header.featuredIn}
						</p>
						<div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-4 md:gap-x-8 md:gap-y-5 max-w-3xl mx-auto px-2">
							{[
								{ src: '/images/Featured/ad.webp', alt: 'Adweek' },
								{ src: '/images/Featured/bi.webp', alt: 'Business Insider' },
								{ src: '/images/Featured/fb.webp', alt: 'Forbes' },
								{ src: '/images/Featured/l.webp', alt: 'Social Media Examiner' },
								{ src: '/images/Featured/tc.webp', alt: 'TechCrunch' },
								{ src: '/images/Featured/wl.webp', alt: 'WordStream' },
							].map((logo) => (
								<img
									key={logo.alt}
									src={logo.src}
									alt={logo.alt}
									className="object-contain"
									style={{
										height: 'clamp(1.15rem, 3.5vw, 1.65rem)',
										width: 'auto',
										maxWidth: '7.5rem',
										opacity: 0.55,
									}}
									loading="lazy"
								/>
							))}
						</div>
					</div>
				</div>


			</div>
		</header>

		{showVideo && (
			<div
				className="fixed inset-0 flex items-center justify-center p-4"
				style={{
					background: 'rgba(10, 37, 64, 0.6)',
					backdropFilter: 'blur(8px)',
					// Above Bootstrap fixed-top (1030) and sticky CTA (1040)
					zIndex: 1100,
				}}
				onClick={() => setShowVideo(false)}
			>
				<div
					className="relative w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl"
					style={{ background: '#000' }}
					onClick={(e) => e.stopPropagation()}
				>
					<button
						onClick={() => setShowVideo(false)}
						className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110 cursor-pointer"
						style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)' }}
					>
						<i className="fa-solid fa-xmark text-white text-sm"></i>
					</button>
					<video
						src="https://i.imgur.com/iyxbD6y.mp4"
						className="w-full"
						style={{ maxHeight: '80vh', objectFit: 'contain' }}
						autoPlay
						loop
						playsInline
						controls
					/>
				</div>
			</div>
		)}

		</>
	);
};

export default Header;
